import {Menu, type WrapperContentPropsFn} from '@dropbox/dig-components/menu';
import {UIIcon} from '@dropbox/dig-icons';
import {
  CheckmarkLine,
  CircleStandardFilledFill,
  CircleStandardLine,
} from '@dropbox/dig-icons/assets';
import {getStatusStyle} from 'components/DSYS/StatusButtonIcon';
import {t} from 'i18next';

import styles from './WorkstreamMetricStatus.module.css';

export const WORKSTREAM_METRIC_STATUS_OPTIONS = [
  {
    value: 'no_status',
    labelKey: 'metric_no_status',
    iconColor: getStatusStyle('not_started').color,
    iconSrc: CircleStandardLine,
  },
  {
    value: 'on_track',
    labelKey: 'on_track',
    iconColor: getStatusStyle('on_track').color,
    iconSrc: CircleStandardFilledFill,
  },
  {
    value: 'at_risk',
    labelKey: 'at_risk',
    iconColor: getStatusStyle('at_risk').color,
    iconSrc: CircleStandardFilledFill,
  },
  {
    value: 'behind',
    labelKey: 'off_track',
    iconColor: getStatusStyle('missed').color,
    iconSrc: CircleStandardFilledFill,
  },
  {
    value: 'closed',
    labelKey: 'closed',
    iconColor: getStatusStyle('closed').color,
    iconSrc: CircleStandardFilledFill,
  },
] as const;

export type WorkstreamMetricStatusOption = (typeof WORKSTREAM_METRIC_STATUS_OPTIONS)[number];

export const getWorkstreamMetricStatusOption = (
  status: string | null | undefined
): WorkstreamMetricStatusOption =>
  WORKSTREAM_METRIC_STATUS_OPTIONS.find((option) => option.value === status) ??
  WORKSTREAM_METRIC_STATUS_OPTIONS[0];

// Closed is retired as a selectable metric status. Metrics that are already
// closed still show it so the current selection stays visible, but once they
// move out of it they cannot move back in.
const getSelectableWorkstreamMetricStatusOptions = (selectedStatus: WorkstreamMetricStatusOption) =>
  WORKSTREAM_METRIC_STATUS_OPTIONS.filter(
    (option) => option.value !== 'closed' || selectedStatus.value === 'closed'
  );

export const WorkstreamMetricStatusTagContent = ({
  iconColor,
  iconSrc,
  labelKey,
  showLabel = true,
}: {
  iconColor: string;
  iconSrc: WorkstreamMetricStatusOption['iconSrc'];
  labelKey: WorkstreamMetricStatusOption['labelKey'];
  showLabel?: boolean;
}) => {
  return (
    <span className={styles.metricStatusContent}>
      <UIIcon src={iconSrc} width={12} height={12} color={iconColor} />
      {showLabel ? <span>{t(labelKey).toString()}</span> : null}
    </span>
  );
};

export const WorkstreamMetricStatusTag = ({
  size = 'xsmall',
  status,
}: {
  size?: 'xsmall' | 'small';
  status?: string | null;
}) => {
  const option = getWorkstreamMetricStatusOption(status);
  const className = `${styles.metricStatusTag} ${
    size === 'small' ? styles.metricStatusTagSmall : ''
  }`;

  return (
    <span className={className}>
      <WorkstreamMetricStatusTagContent
        iconColor={option.iconColor}
        iconSrc={option.iconSrc}
        labelKey={option.labelKey}
      />
    </span>
  );
};

export const WorkstreamMetricStatusMenuContent = ({
  getContentProps,
  includeActionAriaLabel = false,
  onSelect,
  selectedStatus,
  showLabel = true,
}: {
  getContentProps: WrapperContentPropsFn;
  includeActionAriaLabel?: boolean;
  onSelect: (status: WorkstreamMetricStatusOption['value']) => void | Promise<unknown>;
  selectedStatus: WorkstreamMetricStatusOption;
  showLabel?: boolean;
}) => {
  return (
    <Menu.Content {...getContentProps()} placement="bottom-end" style={{minWidth: 160}}>
      <Menu.Segment withLabel={t('metric_progress').toString()}>
        {getSelectableWorkstreamMetricStatusOptions(selectedStatus).map((option) => (
          <Menu.ActionItem
            key={option.value}
            onClick={() => void onSelect(option.value)}
            aria-label={includeActionAriaLabel ? t(option.labelKey).toString() : undefined}
            withRightAccessory={
              selectedStatus.value === option.value ? <UIIcon src={CheckmarkLine} /> : undefined
            }
          >
            <span className={styles.metricStatusTag}>
              <WorkstreamMetricStatusTagContent
                labelKey={option.labelKey}
                iconColor={option.iconColor}
                iconSrc={option.iconSrc}
                showLabel={showLabel}
              />
            </span>
          </Menu.ActionItem>
        ))}
      </Menu.Segment>
    </Menu.Content>
  );
};
